import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Camera, CalendarDays, BookOpen, GraduationCap, MessageSquare, Zap } from "lucide-react";

interface Feature {
  icon: typeof BookOpen;
  shortForm: string;
  action: string;
}

// `action` strings intentionally omit the leading "can" — the header above
// this typewriter text already renders "NOVARI can" as a fixed prefix (see
// the JSX below), so an action starting with "can" duplicated it mid-sentence
// ("NOVARI can" / "can snap phot...").
const features: Feature[] = [
  {
    icon: Camera,
    shortForm: "NOVARI",
    action: "snap photos of notes into quizzes",
  },
  {
    icon: BookOpen,
    shortForm: "NOVARI",
    action: "explain any concept from your actual documents",
  },
  {
    icon: GraduationCap,
    shortForm: "NOVARI",
    action: "simulate real exams with precise timing",
  },
  {
    icon: CalendarDays,
    shortForm: "NOVARI",
    action: "track homework and never let you miss a deadline",
  },
  {
    icon: MessageSquare,
    shortForm: "NOVARI",
    action: "tutor you at your grade level, not like a textbook",
  },
  {
    icon: Zap,
    shortForm: "NOVARI",
    action: "build your mastery with streaks and achievements",
  },
];

export default function TypewriterFeatures() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useReducedMotion();

  const currentFeature = features[currentIndex];
  const fullText = currentFeature.action;

  useEffect(() => {
    if (paused || reducedMotion) return;
    let timeout: NodeJS.Timeout;

    if (isTyping) {
      if (displayText.length < fullText.length) {
        timeout = setTimeout(() => {
          setDisplayText(fullText.slice(0, displayText.length + 1));
        }, 40); // Typing speed
      } else {
        // Hold at full text for 2.5 seconds
        timeout = setTimeout(() => {
          setIsTyping(false);
        }, 2500);
      }
    } else {
      // Clear text
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 30); // Clearing speed
      } else {
        // Move to next feature
        setCurrentIndex((prev) => (prev + 1) % features.length);
        setIsTyping(true);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isTyping, fullText, paused, reducedMotion]);

  return (
    <div className="flex flex-col gap-8">
      {/* Animated feature display */}
      <div className="min-h-[140px] flex items-center">
        <div className="flex flex-col gap-3 w-full">
          <div className="flex items-center gap-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: "rgba(224, 122, 95, 0.1)",
                  border: "1px solid rgba(224, 122, 95, 0.2)",
                }}
              >
                <currentFeature.icon
                  className="w-5 h-5"
                  style={{ color: "var(--accent-primary)" }}
                />
              </motion.div>
            </AnimatePresence>

            <div className="flex items-baseline gap-1">
              <span
                className="text-lg font-bold"
                style={{ color: "var(--text-primary)" }}
              >
                {currentFeature.shortForm}
              </span>
              <span
                className="text-base font-medium"
                style={{ color: "var(--text-secondary)" }}
              >
                can
              </span>
            </div>
          </div>

          {/* Typewriter text with cursor */}
          <div className="flex items-baseline gap-1 min-h-[32px]">
            <span
              className="text-xl font-semibold leading-tight"
              style={{ color: "var(--accent-primary)" }}
            >
              <span className="sr-only">{fullText}</span>
              <span aria-hidden="true">{paused || reducedMotion ? fullText : displayText}</span>
              {!paused && !reducedMotion && <motion.span
                aria-hidden="true"
                animate={{ opacity: [1, 0] }}
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
                className="inline-block w-0.5 h-6 ml-1"
                style={{ background: "var(--accent-primary)" }}
              />}
            </span>
          </div>
        </div>
      </div>

      {/* Feature dots / pagination */}
      <div className="flex items-center gap-2">
        {features.map((feature, index) => (
          <motion.button
            key={index}
            type="button"
            aria-label={`Show feature ${index + 1}: ${feature.action}`}
            aria-pressed={index === currentIndex}
            onClick={() => {
              setCurrentIndex(index);
              setDisplayText("");
              setIsTyping(true);
              setPaused(true);
            }}
            className="flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent-primary)]"
          >
            <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: index === currentIndex ? "var(--accent-primary)" : "var(--border-default)" }} />
          </motion.button>
        ))}
      </div>
      {!reducedMotion && <button type="button" onClick={() => setPaused(value => !value)} className="min-h-11 w-fit rounded-lg px-2 text-sm underline underline-offset-4 focus-visible:outline focus-visible:outline-2" style={{ color: "var(--text-secondary)" }}>{paused ? "Resume features" : "Pause features"}</button>}
    </div>
  );
}
